import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import Header from "./components/layout/Header";
import Sidebar from "./components/layout/Sidebar";
import Agents from "./pages/Agents";
import Assinatura from "./pages/Assinatura";
import Channels from "./pages/Channels";
// LiveKit (videochamada) é pesado — code-split pra não ir no bundle principal de quem nunca faz/recebe chamada.
const AgentCallSession = lazy(() => import("./pages/calls/AgentCallSession"));
const GuestCallPage = lazy(() => import("./pages/calls/GuestCallPage"));
import ClientAccountClaim from "./pages/client/ClientAccountClaim";
const ClientCallPage = lazy(() => import("./pages/client/ClientCallPage"));

const CallPageFallback = () => (
  <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
    Carregando...
  </div>
);
import ClientLogin from "./pages/client/ClientLogin";
import ClientRegister from "./pages/client/ClientRegister";
import ClientTicketDetail from "./pages/client/ClientTicketDetail";
import ClientTickets from "./pages/client/ClientTickets";
import ClientTicketsNewRedirect from "./pages/client/ClientTicketsNewRedirect";
import TenantPortalRedirect from "./pages/client/TenantPortalRedirect";
import Clients from "./pages/Clients";
import Companies from "./pages/Companies";
import Conversations from "./pages/Conversations";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import Index from "./pages/Index";
import KnowledgeBase from "./pages/KnowledgeBase";
import KnowledgeBaseQuestions from "./pages/KnowledgeBaseQuestions";
import Login from "./pages/Login";
import MagicLinkLanding from "./pages/MagicLinkLanding";
import NotFound from "./pages/NotFound";
import Profile from "./pages/Profile";
import Projects from "./pages/Projects";
import Register from "./pages/Register";
import ReportTicketsByClient from "./pages/ReportTicketsByClient";
import Settings from "./pages/Settings";
import SLAs from "./pages/SLAs";
import Teams from "./pages/Teams";
import NewTicket from "./pages/NewTicket";
import TicketDetails from "./pages/TicketDetails";
import Tickets from "./pages/Tickets";
import WhatsAppSettings from "./pages/WhatsAppSettings";

// Verifica se o usuário está autenticado
const isAuthenticated = () => {
  return localStorage.getItem('auth_token') !== null;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-hidden flex flex-col">
        <Header />
        <div className="flex-1 overflow-y-auto p-6 pb-24 lg:pb-6">
          {children}
        </div>
      </main>
    </div>
  );
};

const App = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Configura o tema
    const storedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initialTheme = storedTheme || (prefersDark ? "dark" : "light");

    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(initialTheme);
    localStorage.setItem("theme", initialTheme);

    setLoading(false);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          {/* Magic link — public, no auth required */}
          <Route path="/c/:token" element={<MagicLinkLanding />} />
          <Route path="/:tenant/c/:token" element={<MagicLinkLanding />} />
          {/* Reivindicar conta existente (criada via WhatsApp) confirmando o telefone */}
          <Route path="/c/claim/:token" element={<ClientAccountClaim />} />
          <Route path="/:tenant/c/claim/:token" element={<ClientAccountClaim />} />
          {/* Videochamada (Meet-style) via magic link — cliente clica direto do WhatsApp/notificação, sem sessão prévia */}
          <Route path="/c/:token/chamada/:callId" element={<Suspense fallback={<CallPageFallback />}><ClientCallPage /></Suspense>} />

          <Route path="/cliente/login" element={<ClientLogin />} />
          <Route path="/cliente/cadastre-se" element={<ClientRegister />} />
          <Route path="/cliente/tickets" element={<ClientTickets />} />
          <Route path="/cliente/tickets/new" element={<ClientTicketsNewRedirect />} />
          <Route path="/cliente/tickets/:protocolId" element={<ClientTicketDetail />} />
          <Route path="/cliente/chamada/:callId" element={<Suspense fallback={<CallPageFallback />}><ClientCallPage /></Suspense>} />

          <Route path="/:tenant/cliente/login" element={<ClientLogin />} />
          <Route path="/:tenant/cliente/cadastre-se" element={<ClientRegister />} />
          <Route path="/:tenant/cliente/tickets" element={<ClientTickets />} />
          <Route path="/:tenant/cliente/tickets/new" element={<ClientTicketsNewRedirect />} />
          <Route path="/:tenant/cliente/tickets/:protocolId" element={<ClientTicketDetail />} />
          <Route path="/:tenant/cliente/chamada/:callId" element={<Suspense fallback={<CallPageFallback />}><ClientCallPage /></Suspense>} />
          <Route path="/:tenant" element={<TenantPortalRedirect />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/tickets" element={
            <ProtectedRoute>
              <Tickets />
            </ProtectedRoute>
          } />
          <Route path="/tickets/new" element={
            <ProtectedRoute>
              <NewTicket />
            </ProtectedRoute>
          } />
          <Route path="/tickets/:protocolId" element={
            <ProtectedRoute>
              <TicketDetails />
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          } />
          <Route path="/reports/tickets-por-cliente" element={
            <ProtectedRoute>
              <ReportTicketsByClient />
            </ProtectedRoute>
          } />
          <Route path="/companies" element={
            <ProtectedRoute>
              <Companies />
            </ProtectedRoute>
          } />
          <Route path="/companies/:companyId/projects" element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          } />
          <Route path="/companies/:companyId/projects/:projectId/slas" element={
            <ProtectedRoute>
              <SLAs />
            </ProtectedRoute>
          } />
          <Route path="/channels" element={
            <ProtectedRoute>
              <Channels />
            </ProtectedRoute>
          } />
          <Route path="/teams" element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          } />
          <Route path="/agents" element={
            <ProtectedRoute>
              <Agents />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base" element={
            <ProtectedRoute>
              <KnowledgeBase />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base/:knowledgeBaseId/questions" element={
            <ProtectedRoute>
              <KnowledgeBaseQuestions />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/assinatura" element={
            <ProtectedRoute>
              <Assinatura />
            </ProtectedRoute>
          } />
          <Route path="/conversations" element={
            <ProtectedRoute>
              <Conversations />
            </ProtectedRoute>
          } />
          <Route path="/conversations/:id" element={
            <ProtectedRoute>
              <Conversations />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp-settings" element={
            <ProtectedRoute>
              <WhatsAppSettings />
            </ProtectedRoute>
          } />
          {/* Sessão de atendimento (Meet-style) — aberta em aba própria a partir da conversa, sem chrome do app (Sidebar/Header). */}
          <Route path="/atendimento/:callId" element={<Suspense fallback={<CallPageFallback />}><AgentCallSession /></Suspense>} />
          {/* Convidado entra numa chamada em andamento via link gerado pelo agente — sem login nenhum. */}
          <Route path="/chamada/convidado/:inviteToken" element={<Suspense fallback={<CallPageFallback />}><GuestCallPage /></Suspense>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
